import { useCallback, useEffect } from "react";
import { getThreadMessages } from "../api/message";
import { reconcileFinalBotMessage } from "../pages/chatMessageReconciliation";
import { rememberActiveChatThread } from "../utils/chat_thread_persistence";

const isTempChatThreadUuid = (threadUuid) =>
  String(threadUuid || "").startsWith("temp-");

const isPersistedChatThreadUuid = (threadUuid) => {
  const normalizedThreadUuid = String(threadUuid || "").trim();
  return (
    normalizedThreadUuid.length > 0 &&
    !isTempChatThreadUuid(normalizedThreadUuid)
  );
};

const reconcileHydratedThreadMessages = (currentMessages, hydratedMessages) => {
  if (!Array.isArray(hydratedMessages) || hydratedMessages.length === 0) {
    return hydratedMessages;
  }
  if (!Array.isArray(currentMessages) || currentMessages.length === 0) {
    return hydratedMessages;
  }

  const currentLast = currentMessages[currentMessages.length - 1];
  const hydratedLast = hydratedMessages[hydratedMessages.length - 1];
  if (currentLast?.sender !== "bot" || hydratedLast?.sender !== "bot") {
    return hydratedMessages;
  }

  return [
    ...hydratedMessages.slice(0, -1),
    reconcileFinalBotMessage(currentLast, hydratedLast),
  ];
};

const useChatThreadNavigation = ({
  activeChatThreadPersistenceScope,
  activeThreadRef,
  ensureResponseFlowForThread,
  ensureThreadSubscription,
  freshRequestedChatThread,
  freshResponseFlow,
  hasFreshRequestedPersistedChatThread,
  hydratedMessagesByThreadUuidRef,
  loadedMessagesByThreadUuidRef,
  loadingMessageThreads,
  loadingUserData,
  messageThreadsOwnerRef,
  messagesByThreadUuidRef,
  requestedChatThreadUuid,
  scrollToMessageId,
  setActiveThread,
  setLoadingMessages,
  setMessageThreads,
  setResponseFlow,
  setSkipHideThreadUuid,
  threadHydrationInFlightRef,
  threadPrefetchInFlightRef,
  threadSelectionTokenRef,
  threads,
  updateRequestedChatThreadInUrl,
  updateThreadMessages,
  userId,
}) => {
  const threadNeedsFullHydration = useCallback((messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return false;
    return messages.some((message) => {
      const answeredTickerIds = Array.isArray(message?.answeredTickerIds)
        ? message.answeredTickerIds
        : [];
      return answeredTickerIds.length > 0;
    });
  }, []);

  const prefetchThreadMessages = useCallback(
    async (thread) => {
      const threadUuid = thread?.threadUuid;
      if (!threadUuid || isTempChatThreadUuid(threadUuid)) return null;
      if (activeThreadRef.current?.threadUuid === threadUuid) return null;

      const cachedMessages = messagesByThreadUuidRef.current?.[threadUuid];
      const hasCachedMessages = Object.prototype.hasOwnProperty.call(
        messagesByThreadUuidRef.current || {},
        threadUuid,
      );
      if (
        hasCachedMessages &&
        (Boolean(loadedMessagesByThreadUuidRef.current?.[threadUuid]) ||
          (Array.isArray(cachedMessages) && cachedMessages.length > 0))
      ) {
        return cachedMessages || null;
      }

      const existingRequest = threadPrefetchInFlightRef.current.get(threadUuid);
      if (existingRequest) return existingRequest;

      const request = (async () => {
        try {
          const messages = await getThreadMessages({
            threadUuid,
            lightweight: true,
          });
          loadedMessagesByThreadUuidRef.current = {
            ...(loadedMessagesByThreadUuidRef.current || {}),
            [threadUuid]: true,
          };
          hydratedMessagesByThreadUuidRef.current = {
            ...(hydratedMessagesByThreadUuidRef.current || {}),
            [threadUuid]: !threadNeedsFullHydration(messages),
          };
          updateThreadMessages(threadUuid, messages);
          return messages;
        } catch (error) {
          console.log("Error prefetching thread messages", error);
          return null;
        } finally {
          threadPrefetchInFlightRef.current.delete(threadUuid);
        }
      })();

      threadPrefetchInFlightRef.current.set(threadUuid, request);
      return request;
    },
    [
      activeThreadRef,
      hydratedMessagesByThreadUuidRef,
      loadedMessagesByThreadUuidRef,
      messagesByThreadUuidRef,
      threadNeedsFullHydration,
      threadPrefetchInFlightRef,
      updateThreadMessages,
    ],
  );

  const hydrateThreadMessages = useCallback(
    async (threadUuid, options = {}) => {
      if (!threadUuid || isTempChatThreadUuid(threadUuid)) return null;

      const {
        messageId = null,
        messagesHint = null,
        force = false,
      } = options || {};
      const currentCachedMessages = Array.isArray(messagesHint)
        ? messagesHint
        : messagesByThreadUuidRef.current?.[threadUuid];
      if (!force && !threadNeedsFullHydration(currentCachedMessages)) {
        hydratedMessagesByThreadUuidRef.current = {
          ...(hydratedMessagesByThreadUuidRef.current || {}),
          [threadUuid]: true,
        };
        return currentCachedMessages || null;
      }

      const existingRequest = threadHydrationInFlightRef.current.get(threadUuid);
      if (existingRequest) return existingRequest;

      const request = (async () => {
        try {
          const fullMessages = await getThreadMessages({
            threadUuid,
            messageId,
          });
          loadedMessagesByThreadUuidRef.current = {
            ...(loadedMessagesByThreadUuidRef.current || {}),
            [threadUuid]: true,
          };
          hydratedMessagesByThreadUuidRef.current = {
            ...(hydratedMessagesByThreadUuidRef.current || {}),
            [threadUuid]: true,
          };
          const messagesForState = reconcileHydratedThreadMessages(
            messagesByThreadUuidRef.current?.[threadUuid],
            fullMessages,
          );
          updateThreadMessages(threadUuid, messagesForState);
          return messagesForState;
        } catch (error) {
          console.log("Error hydrating thread messages", error);
          return null;
        } finally {
          threadHydrationInFlightRef.current.delete(threadUuid);
        }
      })();

      threadHydrationInFlightRef.current.set(threadUuid, request);
      return request;
    },
    [
      hydratedMessagesByThreadUuidRef,
      loadedMessagesByThreadUuidRef,
      messagesByThreadUuidRef,
      threadHydrationInFlightRef,
      threadNeedsFullHydration,
      updateThreadMessages,
    ],
  );

  const selectActiveThread = useCallback(
    async (thread, scrollMessageId = null) => {
      if (!thread) return;
      if (isPersistedChatThreadUuid(thread?.threadUuid)) {
        updateRequestedChatThreadInUrl(thread.threadUuid);
      } else if (
        isTempChatThreadUuid(thread?.threadUuid) &&
        !thread?.pendingRealThread
      ) {
        updateRequestedChatThreadInUrl(null);
      }

      if (
        !isTempChatThreadUuid(thread?.threadUuid) ||
        thread?.pendingRealThread
      ) {
        rememberActiveChatThread(thread, activeChatThreadPersistenceScope);
      }

      const selectionToken = threadSelectionTokenRef.current + 1;
      threadSelectionTokenRef.current = selectionToken;
      const selectedThreadUuid = thread?.threadUuid ?? null;
      const isStillSelectedThread = () =>
        threadSelectionTokenRef.current === selectionToken &&
        (activeThreadRef.current?.threadUuid ?? null) === selectedThreadUuid;

      setActiveThread(thread);
      activeThreadRef.current = thread;

      if (thread?.threadUuid) {
        const flow = ensureResponseFlowForThread(thread.threadUuid, {
          hideQuestionButtons:
            isTempChatThreadUuid(thread.threadUuid) && !userId,
        });
        if (flow) setResponseFlow(flow);
      } else {
        setResponseFlow(freshResponseFlow());
      }

      const cachedMessagesByThreadUuid = messagesByThreadUuidRef.current || {};
      if (isTempChatThreadUuid(thread?.threadUuid)) {
        if (thread?.pendingRealThread) {
          ensureThreadSubscription(thread.threadUuid, { setAsPrimary: true });
        }
        const cachedTempMessages =
          cachedMessagesByThreadUuid?.[thread.threadUuid];
        setSkipHideThreadUuid(null);
        messageThreadsOwnerRef.current = thread.threadUuid;
        setMessageThreads(
          Array.isArray(cachedTempMessages) ? cachedTempMessages : [],
        );
        setLoadingMessages(false);
        return;
      }

      const cachedThreadMessages =
        cachedMessagesByThreadUuid?.[thread.threadUuid];
      const hasCachedThreadMessages = Object.prototype.hasOwnProperty.call(
        cachedMessagesByThreadUuid,
        thread.threadUuid,
      );
      const shouldUseCachedThreadMessages =
        hasCachedThreadMessages &&
        (Boolean(loadedMessagesByThreadUuidRef.current?.[thread.threadUuid]) ||
          (Array.isArray(cachedThreadMessages) &&
            cachedThreadMessages.length > 0));

      if (shouldUseCachedThreadMessages) {
        setSkipHideThreadUuid(thread.threadUuid);
        messageThreadsOwnerRef.current = thread.threadUuid;
        setMessageThreads(
          Array.isArray(cachedThreadMessages) ? cachedThreadMessages : [],
        );
        if (
          !hydratedMessagesByThreadUuidRef.current?.[thread.threadUuid] &&
          threadNeedsFullHydration(cachedThreadMessages)
        ) {
          hydrateThreadMessages(thread.threadUuid, {
            messageId: scrollMessageId || scrollToMessageId || null,
            messagesHint: cachedThreadMessages,
          });
        }
        return;
      }

      try {
        setSkipHideThreadUuid(null);
        setLoadingMessages(true);
        const targetMessageId = scrollMessageId || scrollToMessageId || null;
        const messages = await getThreadMessages({
          threadUuid: thread.threadUuid,
          messageId: targetMessageId,
          lightweight: true,
        });
        if (!isStillSelectedThread()) return;
        loadedMessagesByThreadUuidRef.current = {
          ...(loadedMessagesByThreadUuidRef.current || {}),
          [thread.threadUuid]: true,
        };
        hydratedMessagesByThreadUuidRef.current = {
          ...(hydratedMessagesByThreadUuidRef.current || {}),
          [thread.threadUuid]: !threadNeedsFullHydration(messages),
        };
        messageThreadsOwnerRef.current = thread.threadUuid;
        updateThreadMessages(thread.threadUuid, messages);
        if (threadNeedsFullHydration(messages)) {
          hydrateThreadMessages(thread.threadUuid, {
            messageId: targetMessageId,
            messagesHint: messages,
          });
        }
      } catch (error) {
        if (isStillSelectedThread()) {
          messageThreadsOwnerRef.current = thread.threadUuid;
          setMessageThreads([]);
        }
        console.log("Error fetching thread messages", error);
      } finally {
        if (isStillSelectedThread()) {
          setLoadingMessages(false);
        }
      }
    },
    [
      activeChatThreadPersistenceScope,
      activeThreadRef,
      ensureResponseFlowForThread,
      ensureThreadSubscription,
      freshResponseFlow,
      hydrateThreadMessages,
      hydratedMessagesByThreadUuidRef,
      loadedMessagesByThreadUuidRef,
      messageThreadsOwnerRef,
      messagesByThreadUuidRef,
      scrollToMessageId,
      setActiveThread,
      setLoadingMessages,
      setMessageThreads,
      setResponseFlow,
      setSkipHideThreadUuid,
      threadNeedsFullHydration,
      threadSelectionTokenRef,
      updateThreadMessages,
      updateRequestedChatThreadInUrl,
      userId,
    ],
  );

  useEffect(() => {
    if (!hasFreshRequestedPersistedChatThread) return;
    if (loadingUserData || loadingMessageThreads) return;
    if (activeThreadRef.current?.threadUuid === requestedChatThreadUuid) {
      return;
    }

    const safeThreads = Array.isArray(threads) ? threads : [];
    const requestedThread =
      safeThreads.find(
        (thread) => thread?.threadUuid === requestedChatThreadUuid,
      ) || {
        threadUuid: requestedChatThreadUuid,
        title: freshRequestedChatThread?.title || "Chat",
        isAdminTargeted: false,
      };

    selectActiveThread(requestedThread);
  }, [
    activeThreadRef,
    freshRequestedChatThread?.title,
    hasFreshRequestedPersistedChatThread,
    loadingMessageThreads,
    loadingUserData,
    requestedChatThreadUuid,
    selectActiveThread,
    threads,
  ]);

  return {
    hydrateThreadMessages,
    prefetchThreadMessages,
    selectActiveThread,
    threadNeedsFullHydration,
  };
};

export default useChatThreadNavigation;
